import { createSlice } from "@reduxjs/toolkit";

/**
 * Redux slice for managing scheduled analysis admin page state
 * @module scheduledAnalysisSlice
 */
const scheduledAnalysisSlice = createSlice({
  name: "scheduledAnalysis",
  initialState: {
    schedules: [], // List of scheduled analyses
    loading: false, // Loading state for fetching schedules
    error: null, // Error message
    refreshing: false, // Refresh loading state
    selectedSchedule: null, // Currently selected schedule for view/edit
    modalOpen: false, // Create/Edit modal state
    modalMode: 'create', // 'create' or 'edit'
    confirmOpen: false, // Delete confirmation dialog state
    availableFrameworks: [], // Frameworks available for selected dataset
    availableClients: [], // [{name, datasets: [...]}] from available-clients API
    clientFrameworkMap: {}, // Cache: { "united": { "sales_data": [...frameworks] } }
    loadingClients: false, // Loading state for clients API call
    availableFilters: [], // filter_groups from available-filters API
    loadingFilters: false, // Loading state for filters API call
    saving: false, // Saving state for create/update operations
    filters: {
      client: 'all',
      status: 'all',
      searchQuery: '',
    },
  },
  reducers: {
    /**
     * Set the list of scheduled analyses
     */
    setSchedules: (state, action) => {
      state.schedules = action.payload;
      state.loading = false;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.refreshing = false;
      state.saving = false;
    },

    /**
     * Set refreshing state
     */
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    /**
     * Set the currently selected schedule
     */
    setSelectedSchedule: (state, action) => {
      state.selectedSchedule = action.payload;
    },

    /**
     * Set modal open/closed state
     */
    setModalOpen: (state, action) => {
      state.modalOpen = action.payload;
      if (!action.payload) {
        state.selectedSchedule = null;
        state.modalMode = 'create';
      }
    },

    /**
     * Set modal mode (create or edit)
     */
    setModalMode: (state, action) => {
      state.modalMode = action.payload;
    },

    /**
     * Set confirmation dialog state
     */
    setConfirmOpen: (state, action) => {
      state.confirmOpen = action.payload;
    },

    /**
     * Set available frameworks for a dataset
     */
    setAvailableFrameworks: (state, action) => {
      state.availableFrameworks = action.payload;
    },

    /**
     * Set available clients from API
     */
    setAvailableClients: (state, action) => {
      state.availableClients = action.payload;
      state.loadingClients = false;
    },

    /**
     * Set loading state for clients fetch
     */
    setLoadingClients: (state, action) => {
      state.loadingClients = action.payload;
    },

    /**
     * Cache framework data per client
     * Payload: { client: "united", datasets: [{ name: "sales_data", frameworks: [...] }] }
     */
    setClientFrameworkMap: (state, action) => {
      const { client, datasets } = action.payload;
      const map = {};
      (datasets || []).forEach(d => {
        map[d.name] = d.frameworks || [];
      });
      state.clientFrameworkMap[client] = map;
    },

    /**
     * Set available filters from API
     */
    setAvailableFilters: (state, action) => {
      state.availableFilters = action.payload;
      state.loadingFilters = false;
    },

    /**
     * Set loading state for filters fetch
     */
    setLoadingFilters: (state, action) => {
      state.loadingFilters = action.payload;
    },

    /**
     * Set saving state
     */
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    /**
     * Set filters
     */
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Update a single schedule in the list (after edit or status change)
     */
    updateScheduleInList: (state, action) => {
      const updated = action.payload;
      const index = state.schedules.findIndex(s => s.analysis_id === updated.analysis_id);
      if (index !== -1) {
        state.schedules[index] = updated;
      }
    },

    /**
     * Remove a schedule from the list (after delete)
     */
    removeScheduleFromList: (state, action) => {
      const analysisId = action.payload;
      state.schedules = state.schedules.filter(s => s.analysis_id !== analysisId);
    },

    /**
     * Add a new schedule to the list
     */
    addScheduleToList: (state, action) => {
      state.schedules.unshift(action.payload);
    },
  },
});

export const {
  setSchedules,
  setLoading,
  setError,
  setRefreshing,
  setSelectedSchedule,
  setModalOpen,
  setModalMode,
  setConfirmOpen,
  setAvailableFrameworks,
  setAvailableClients,
  setLoadingClients,
  setClientFrameworkMap,
  setAvailableFilters,
  setLoadingFilters,
  setSaving,
  setFilters,
  clearError,
  updateScheduleInList,
  removeScheduleFromList,
  addScheduleToList,
} = scheduledAnalysisSlice.actions;

export default scheduledAnalysisSlice.reducer;
